/**
 * Shopify 浮动购物车功能
 * 使用 Shopify Cart API
 * 优化版本 - 可靠的自动更新机制
 */

(function() {
  'use strict';

  // 防止重复初始化
  if (window.FloatingCartInitialized) {
    console.log('[浮動購物車] 已初始化，跳過重複執行');
    return;
  }
  window.FloatingCartInitialized = true;

  class FloatingCart {
    constructor() {
      this.cartData = null;
      this.isOpen = false;
      this.isLoading = false;
      this.previousItemCount = 0;
      this.pollInterval = null;
      this.init();
    }

    init() {
      try {
        // 绑定事件
        this.bindEvents();
        // 初始化加载购物车
        this.loadCart();
        // 监听购物车更新事件
        this.listenForCartUpdates();
        // 拦截添加到购物车的操作
        this.interceptAddToCart();
        // 启动智能轮询（仅作为备用）
        this.startSmartPolling();
      } catch (error) {
        console.error('[浮動購物車] 初始化失敗:', error);
      }
    }

    bindEvents() {
      try {
        // 打开购物车
        const floatingBtn = document.getElementById('floating-cart-btn');
        if (floatingBtn) {
          floatingBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.openCart();
          });
        }

        // 关闭购物车
        const closeBtn = document.getElementById('close-cart-btn');
        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.closeCart();
          });
        }

        // 点击遮罩关闭
        const drawer = document.getElementById('cart-drawer');
        if (drawer) {
          const overlay = drawer.querySelector('.cart-drawer-overlay');
          if (overlay) {
            overlay.addEventListener('click', (e) => {
              e.preventDefault();
              this.closeCart();
            });
          }
        }
      } catch (error) {
        console.error('[浮動購物車] 事件綁定失敗:', error);
      }
    }

    openCart() {
      try {
        const drawer = document.getElementById('cart-drawer');
        if (drawer) {
          drawer.classList.add('active');
          this.isOpen = true;
          document.body.style.overflow = 'hidden';
          // 重新加载购物车数据
          this.loadCart();
        }
      } catch (error) {
        console.error('[浮動購物車] 打開購物車失敗:', error);
      }
    }

    closeCart() {
      try {
        const drawer = document.getElementById('cart-drawer');
        if (drawer) {
          drawer.classList.remove('active');
          this.isOpen = false;
          document.body.style.overflow = '';
        }
      } catch (error) {
        console.error('[浮動購物車] 關閉購物車失敗:', error);
      }
    }

    async loadCart() {
      if (this.isLoading) return;
      
      this.isLoading = true;
      try {
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const newCartData = await response.json();
        const itemCountChanged = this.cartData && newCartData.item_count !== this.cartData.item_count;
        
        this.cartData = newCartData;
        this.updateCartUI();

        // 如果数量变化了，记录日志
        if (itemCountChanged) {
          console.log('[浮動購物車] 購物車數量已更新:', this.cartData.item_count);
        }
      } catch (error) {
        console.error('[浮動購物車] 加載購物車失敗:', error);
        this.showError('無法加載購物車數據');
      } finally {
        this.isLoading = false;
      }
    }

    updateCartUI() {
      try {
        if (!this.cartData) return;

        // 更新购物车数量徽章
        const badge = document.getElementById('cart-count-badge');
        const totalCount = document.getElementById('cart-total-count');
        const itemCount = this.cartData.item_count || 0;

        if (badge) {
          badge.textContent = itemCount > 0 ? itemCount : '';
          badge.style.display = itemCount > 0 ? 'flex' : 'none';
        }
        if (totalCount) {
          totalCount.textContent = itemCount;
        }

        // 更新商品列表
        this.renderCartItems();

        // 更新总价
        this.updateCartTotal();

        // P0f (2026-05-02): 根据 cart 内 SKU 动态更新「結帳」按钮 href（带 ?nsp=1 或不带）
        this.updateCheckoutButtonForNsp();

        // 更新之前的数量记录
        this.previousItemCount = itemCount;
      } catch (error) {
        console.error('[浮動購物車] 更新UI失敗:', error);
      }
    }

    /**
     * P0f (2026-05-02): No-Store-Pickup SKU 入口 2 — 浮動購物車結帳按鈕
     * 根據 cart 內 SKU 動態更新 #cart-checkout-btn href：
     *   - 含 NSP → /pages/cod-checkout?nsp=1
     *   - 不含  → /pages/cod-checkout
     * fetch 失敗 → 不改 href（server createCodOrderService 兜底）
     */
    async updateCheckoutButtonForNsp() {
      try {
        const btn = document.getElementById('cart-checkout-btn');
        if (!btn || !window.COD_NSP || !this.cartData) return;
        const skus = await window.COD_NSP.fetchSkus();
        if (!skus) return;
        const hasNsp = (this.cartData.items || []).some(function (item) {
          return skus.has((item.sku || '').trim());
        });
        const baseUrl = '/pages/cod-checkout';
        btn.href = hasNsp ? baseUrl + '?nsp=1' : baseUrl;
        if (hasNsp) console.log('[COD_NSP] floating-cart → blocked SKU → ?nsp=1');
      } catch (err) {
        console.warn('[COD_NSP] updateCheckoutButtonForNsp failed:', err);
      }
    }

    renderCartItems() {
      try {
        const container = document.getElementById('cart-items-container');
        if (!container) return;

        // 清空当前内容
        container.innerHTML = '';

        if (!this.cartData || !this.cartData.items || this.cartData.items.length === 0) {
          container.innerHTML = '<div class="cart-empty-message"><p>購物車是空的</p></div>';
          return;
        }

        // 获取模板
        const template = document.getElementById('cart-item-template');
        if (!template) {
          console.error('[浮動購物車] 找不到商品模板');
          return;
        }

        this.cartData.items.forEach((item, index) => {
          try {
            const itemElement = template.content.cloneNode(true);
            const cartItem = itemElement.querySelector('.cart-item');

            // 设置数据属性
            if (cartItem) {
              cartItem.setAttribute('data-line-item-key', item.key);
              cartItem.setAttribute('data-line-index', index + 1);
            }

            // 图片
            const img = itemElement.querySelector('.cart-item-img');
            if (img && item.image) {
              img.src = item.image;
              img.alt = item.product_title || '';
            }

            // 标题和价格
            const title = itemElement.querySelector('.cart-item-title');
            if (title) {
              title.textContent = item.product_title || '';
            }

            const price = itemElement.querySelector('.cart-item-price');
            if (price) {
              price.textContent = this.formatMoney(item.price);
            }

            // 规格
            const variant = itemElement.querySelector('.cart-item-variant');
            if (variant) {
              if (item.variant_title && item.variant_title !== 'Default Title') {
                variant.textContent = item.variant_title;
              } else {
                variant.style.display = 'none';
              }
            }

            // 商品总价
            const total = itemElement.querySelector('.cart-item-total');
            if (total) {
              total.textContent = this.formatMoney(item.line_price);
            }

            // 数量
            const quantityValue = itemElement.querySelector('.quantity-value');
            if (quantityValue) {
              quantityValue.textContent = item.quantity || 0;
            }

            // 数量按钮
            const decreaseBtn = itemElement.querySelector('[data-action="decrease"]');
            const increaseBtn = itemElement.querySelector('[data-action="increase"]');
            const removeBtn = itemElement.querySelector('[data-action="remove"]');

            if (decreaseBtn) {
              decreaseBtn.addEventListener('click', (e) => {
                e.preventDefault();
                // Stage 2E-2: 帶 meta(item + delta=-1)→ updateQuantity 內判定不送 AddToCart
                this.updateQuantity(item.key, item.quantity - 1, { item: item, delta: -1 });
              });
            }
            
            if (increaseBtn) {
              increaseBtn.addEventListener('click', (e) => {
                e.preventDefault();
                // Stage 2E-2: 帶 meta(item + delta=+1)→ updateQuantity 內觸發 AddToCart
                this.updateQuantity(item.key, item.quantity + 1, { item: item, delta: +1 });
              });
            }
            
            if (removeBtn) {
              removeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.removeItem(item.key);
              });
            }

            container.appendChild(itemElement);
          } catch (itemError) {
            console.error('[浮動購物車] 渲染商品失敗:', itemError);
          }
        });
      } catch (error) {
        console.error('[浮動購物車] 渲染商品列表失敗:', error);
      }
    }

    updateCartTotal() {
      try {
        const subtotalElement = document.getElementById('cart-subtotal');
        if (subtotalElement && this.cartData) {
          subtotalElement.textContent = this.formatMoney(this.cartData.total_price) + ' TWD';
        }
      } catch (error) {
        console.error('[浮動購物車] 更新總價失敗:', error);
      }
    }

    async updateQuantity(key, quantity, meta) {
      if (quantity < 0) return;

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: key,
            quantity: quantity
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        this.cartData = await response.json();
        this.updateCartUI();

        // Stage 2E-2 (2026-05-07): 只在 +1 時送 AddToCart,-1/0 不送(避 Meta 收到負面信號)
        // TD-35 (2026-05-13): title 4 層 fallback 對齊 homie-track.js L189
        try {
          if (meta && meta.delta > 0 && meta.item && window.HomieTrack && window.HomieTrack.addToCart) {
            window.HomieTrack.addToCart({
              variant_id: meta.item.variant_id || meta.item.id,
              product_id: meta.item.product_id,
              title: meta.item.product_title
                  || meta.item.title
                  || (meta.item.dataset && meta.item.dataset.productTitle)
                  || (document.querySelector(`[data-variant-id="${meta.item.variant_id}"] [data-product-title]`)?.dataset?.productTitle)
                  || '',
              price: Number(meta.item.price) / 100, // cart.js item.price 是 cents
              quantity: 1,
              source: 'floating-cart-inc'
            });
          }
        } catch (_) {}

        // 触发购物车更新事件
        this.triggerCartUpdate();
      } catch (error) {
        console.error('[浮動購物車] 更新數量失敗:', error);
        this.showError('更新數量失敗，請重試');
      }
    }

    async removeItem(key) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: key,
            quantity: 0
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        this.cartData = await response.json();
        this.updateCartUI();
        
        // 触发购物车更新事件
        this.triggerCartUpdate();
      } catch (error) {
        console.error('[浮動購物車] 刪除商品失敗:', error);
        this.showError('刪除商品失敗，請重試');
      }
    }

    formatMoney(cents) {
      try {
        if (cents === null || cents === undefined) return 'NT$0';
        return 'NT$' + (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      } catch (error) {
        console.error('[浮動購物車] 格式化金額失敗:', error);
        return 'NT$0';
      }
    }

    triggerCartUpdate() {
      try {
        // 触发自定义事件，通知其他组件购物车已更新
        const event = new CustomEvent('cart:updated', {
          detail: { cart: this.cartData, source: 'floating-cart' },
          bubbles: true,
          cancelable: true
        });
        document.dispatchEvent(event);
      } catch (error) {
        console.error('[浮動購物車] 觸發更新事件失敗:', error);
      }
    }

    // 拦截添加到购物车的操作 - 优化版本
    interceptAddToCart() {
      // 避免重复拦截
      if (window._floatingCartFetchIntercepted) return;
      window._floatingCartFetchIntercepted = true;

      try {
        const self = this;
        const originalFetch = window.fetch;
        
        window.fetch = function(...args) {
          // 获取 URL（支持字符串和 Request 对象）
          let url = args[0];
          if (url instanceof Request) {
            url = url.url;
          }
          
          // 调用原始 fetch
          const promise = originalFetch.apply(this, args);
          
          // 检测是否是添加到购物车的请求
          if (typeof url === 'string' && (url.includes('/cart/add') || url.includes('/cart/change'))) {
            promise.then(() => {
              console.log('[浮動購物車] 檢測到購物車更新請求');
              setTimeout(() => self.loadCart(), 400);
            }).catch(() => {
              // 静默处理错误
            });
          }
          
          return promise;
        };

        console.log('[浮動購物車] 已啟用 fetch 攔截');
      } catch (error) {
        console.error('[浮動購物車] 攔截 fetch 失敗:', error);
      }
    }

    listenForCartUpdates() {
      try {
        // 监听各种可能的购物车事件
        const events = [
          'cart:updated',
          'cart:add', 
          'cart:refresh',
          'cart.requestComplete',
          'product:added',
          'ajaxCart.afterCartLoad'
        ];

        events.forEach(eventName => {
          document.addEventListener(eventName, (e) => {
            // 避免循环
            if (e.detail && e.detail.source === 'floating-cart') return;
            
            console.log('[浮動購物車] 檢測到事件:', eventName);
            this.loadCart();
          });
        });

        // jQuery 事件（如果存在）
        if (window.jQuery) {
          window.jQuery(document).on('cart.requestComplete', () => {
            console.log('[浮動購物車] 檢測到 jQuery 購物車事件');
            this.loadCart();
          });
        }

        console.log('[浮動購物車] 已啟用事件監聽');
      } catch (error) {
        console.error('[浮動購物車] 監聽事件失敗:', error);
      }
    }

    // 智能轮询 - 仅在必要时运行
    startSmartPolling() {
      try {
        let consecutiveNoChanges = 0;
        const maxNoChanges = 10; // 连续 10 次无变化后降低频率
        
        this.pollInterval = setInterval(() => {
          // 购物车打开时不轮询
          if (this.isOpen || this.isLoading) return;
          
          this.checkCartChange().then(changed => {
            if (changed) {
              consecutiveNoChanges = 0;
            } else {
              consecutiveNoChanges++;
              
              // 如果连续多次无变化，降低轮询频率
              if (consecutiveNoChanges >= maxNoChanges) {
                clearInterval(this.pollInterval);
                // 改为 10 秒一次
                this.pollInterval = setInterval(() => this.checkCartChange(), 10000);
                console.log('[浮動購物車] 降低輪詢頻率至 10 秒');
              }
            }
          });
        }, 3000); // 初始 3 秒检查一次
        
        console.log('[浮動購物車] 已啟用智能輪詢（3秒間隔）');
      } catch (error) {
        console.error('[浮動購物車] 啟動輪詢失敗:', error);
      }
    }

    async checkCartChange() {
      try {
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) return false;
        
        const newCartData = await response.json();
        const newItemCount = newCartData.item_count || 0;
        
        // 如果数量发生变化，更新 UI
        if (this.previousItemCount !== newItemCount) {
          console.log('[浮動購物車] 輪詢檢測到變化:', this.previousItemCount, '->', newItemCount);
          this.cartData = newCartData;
          this.updateCartUI();
          return true;
        }
        return false;
      } catch (error) {
        return false;
      }
    }

    showError(message) {
      try {
        const container = document.getElementById('cart-items-container');
        if (container) {
          container.innerHTML = `<div class="cart-empty-message"><p>${message}</p></div>`;
        }
      } catch (error) {
        console.error('[浮動購物車] 顯示錯誤信息失敗:', error);
      }
    }
  }

  // 安全初始化浮动购物车
  function initFloatingCart() {
    try {
      const floatingBtn = document.getElementById('floating-cart-btn');
      if (floatingBtn) {
        window.floatingCartInstance = new FloatingCart();
        console.log('[浮動購物車] 初始化成功');
      } else {
        console.warn('[浮動購物車] 未找到浮動按鈕元素');
      }
    } catch (error) {
      console.error('[浮動購物車] 初始化失敗:', error);
    }
  }

  // 根据文档加载状态初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFloatingCart);
  } else {
    initFloatingCart();
  }
})();
